import { useState, useEffect } from "react";
import { Box } from "@mui/material";
import { useNavigate, useParams } from "react-router";
import { useFormik } from "formik";
import * as Yup from "yup";
import { request } from "../../config/request.ts";
import { toast } from "react-toastify";
import PageHeader from "../../shared/PageHeader/index.tsx";
import TextField from "../../shared/TextField/index.tsx";
import SelectField from "../../shared/SelectField/index.tsx";
import DatePicker from "../../shared/DatePicker/index.tsx";
import AutocompleteField from "../../shared/AutocompleteField/index.tsx";
import AutoCompleteMultiSelect from "../../shared/AutoCompleteMultiSelect/index.tsx";
import PageWrapper from "../../shared/PageWrapper/index.tsx";
import dayjs from "dayjs";
import "../../styles/scss/Form.scss";

export default function EntryCreateEdit() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [selectedType, setSelectedType] = useState("Expense");

  const formik = useFormik({
    initialValues: {
      transactionDate: dayjs().format("YYYY-MM-DD"),
      transactionTime: dayjs().format("HH:mm"),
      type: "Expense",
      ledgerId: null as number | null,
      amount: "",
      accountId: null as number | null,
      employeeIds: [] as number[],
      remarks: "",
    },
    validationSchema: Yup.object({
      transactionDate: Yup.string().required("Date is required"),
      transactionTime: Yup.string().required("Time is required"),
      type: Yup.string().required("Type is required"),
      ledgerId: Yup.number().nullable().required("Ledger is required"),
      amount: Yup.string().required("Amount is required"),
      accountId: Yup.number().nullable().required("Account is required"),
    }),
    onSubmit: async (values) => {
      try {
        const payload = {
          transactionDate: values.transactionDate,
          transactionTime: values.transactionTime,
          type: values.type,
          ledgerId: values.ledgerId,
          amount: values.amount ? parseFloat(values.amount) : 0,
          accountId: values.accountId,
          employeeIds: values.employeeIds,
          remarks: values.remarks,
        };
        let url = "/api/createTransaction";
        let method = "post";
        if (id) {
          url = `/api/updateTransaction/${id}`;
          method = "put";
        }

        const response = await request[method](url, payload);

        if (response?.data?.status) {
          toast.success(response?.data?.message || "Entry saved successfully!");
          navigate("/admin/entries");
        }
      } catch (error) {
        toast.error(
          error?.response?.data?.message ||
            "An error occurred. Please try again.",
        );
      }
    },
  });

  useEffect(() => {
    setSelectedType(formik.values.type);
  }, [formik.values.type]);

  useEffect(() => {
    if (id) {
      const fetchEntry = async () => {
        setLoading(true);
        try {
          const response = await request.get(`/api/getTransactionById/${id}`);
          const entry = response.data.data;
          formik.setValues({
            transactionDate:
              entry?.transactionDate || dayjs().format("YYYY-MM-DD"),
            transactionTime: entry?.transactionTime || dayjs().format("HH:mm"),
            type: entry?.type || "Expense",
            ledgerId: entry?.ledgerId || null,
            amount: entry?.amount ?? "",
            accountId: entry?.accountId || null,
            employeeIds: entry?.employeeIds || [],
            remarks: entry?.remarks || "",
          });
          setLoading(false);
        } catch (error) {
          console.log(error);
          setLoading(false);
        }
      };
      fetchEntry();
    }
  }, [id]);

  return (
    <PageWrapper loading={loading}>
      <Box className="form-container">
        <Box className="form-page">
          <Box className="form-header">
            <PageHeader
              title={id ? "Edit Entry" : "Create New Entry"}
              secondaryButton={{
                label: "Cancel",
                onClick: () => navigate("/admin/entries"),
              }}
              primaryButton={{
                label: id ? "Update Entry" : "Create Entry",
                icon: <span className="material-symbols-outlined">save</span>,
                buttonLoading: formik.isSubmitting,
                onClick: () => formik.handleSubmit(),
              }}
            />
          </Box>

          <Box className="form-card">
            <Box className="form-card-body">
              <Box className="form-grid form-grid--2" sx={{ mb: 1 }}>
                <Box className="form-field">
                  <DatePicker
                    name="transactionDate"
                    label="Transaction Date"
                    formik={formik}
                  />
                </Box>
                <Box className="form-field">
                  <TextField
                    name="transactionTime"
                    label="Transaction Time"
                    type="time"
                    formik={formik}
                  />
                </Box>
              </Box>

              <Box className="form-grid form-grid--2" sx={{ mb: 1 }}>
                <Box className="form-field">
                  <SelectField
                    name="type"
                    label="Type"
                    formik={formik}
                    options={[
                      { value: "Income", label: "Income" },
                      { value: "Expense", label: "Expense" },
                    ]}
                  />
                </Box>
                <Box className="form-field">
                  <AutocompleteField
                    key={`ledger-${selectedType}`}
                    name="ledgerId"
                    label="Ledger"
                    formik={formik}
                    apiUrl="/api/getActiveLedgers"
                    apiPayload={{ ledgerType: selectedType }}
                    apiMethod={"get"}
                    optionLabelKey="ledgerName"
                    optionValueKey="id"
                  />
                </Box>
              </Box>

              <Box className="form-grid form-grid--2" sx={{ mb: 1 }}>
                <Box className="form-field">
                  <TextField
                    name="amount"
                    label="Amount"
                    type="number"
                    formik={formik}
                  />
                </Box>
                <Box className="form-field">
                  <AutocompleteField
                    key="account"
                    name="accountId"
                    label="Bank Account"
                    formik={formik}
                    apiUrl="/api/staff/getActiveAccounts"
                    apiMethod={"get"}
                    optionLabelKey="accountName"
                    optionValueKey="id"
                  />
                </Box>
              </Box>

              <Box className="form-grid form-grid--2" sx={{ mb: 1 }}>
                <Box className="form-field">
                  <AutoCompleteMultiSelect
                    name="employeeIds"
                    label="Employees"
                    formik={formik}
                    apiUrl="/api/getCurrentEmployees"
                    apiMethod={"get"}
                    optionLabelKey="name"
                    optionValueKey="id"
                  />
                </Box>
                <Box
                  className="form-field"
                  sx={{ display: "flex", alignItems: "center" }}
                >
                  {formik.values.type === "Expense" &&
                    Number(formik.values.amount) > 0 &&
                    (formik.values.employeeIds?.length > 0 ? (
                      <Box
                        sx={{
                          p: 1.5,
                          borderRadius: "8px",
                          background: "rgba(37, 99, 235, 0.08)",
                          border: "1px solid rgba(37, 99, 235, 0.15)",
                          fontSize: "0.8125rem",
                          fontWeight: 500,
                          color: "#1e40af",
                          width: "100%",
                        }}
                      >
                        ₹{Number(formik.values.amount).toLocaleString()} equally
                        split — ₹
                        {(
                          Number(formik.values.amount) /
                          formik.values.employeeIds.length
                        ).toLocaleString(undefined, {
                          maximumFractionDigits: 2,
                        })}{" "}
                        each
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          p: 1.5,
                          borderRadius: "8px",
                          background: "rgba(245, 158, 11, 0.08)",
                          border: "1px solid rgba(245, 158, 11, 0.15)",
                          fontSize: "0.8125rem",
                          fontWeight: 500,
                          color: "#92400e",
                          width: "100%",
                        }}
                      >
                        Select employees for amount split
                      </Box>
                    ))}
                </Box>
              </Box>

              <Box className="form-grid form-grid--2" sx={{ mt: 1 }}>
                <Box className="form-field">
                  <TextField
                    name="remarks"
                    label="Remarks"
                    type="text"
                    formik={formik}
                    multiline
                    rows={3}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </PageWrapper>
  );
}
